import { computed, ref, watch } from 'vue';
import { defineStore } from 'pinia';
import { useRouter, useRoute } from 'vue-router';

import { useUserStore } from './user';
import {
  getDemoEventAnalytics,
  getEventById,
  getEventTemplateById,
  getReportedEvents,
} from '@/utils/api';
import { filterAvailableWidgets } from '@/utils/widgetFilters';

import type { ReportableEvent } from '@/types/Analytics';
import type { EventType } from '@/types/Event';
import type { WidgetObject, WidgetObjectSorted, WidgetOptions } from '@/types/Widget';

export const useAnalyticsStore = defineStore('analytics', () => {
  const router = useRouter();
  const route = useRoute();
  const userStore = useUserStore();

  const loading = ref(false);

  // Selected Event from Query Params
  const chosenEventId = ref<number | null>(null);

  // Possible Events
  const reportableEvents = ref<ReportableEvent[]>([]);

  // Selected Event
  const chosenEvent = ref<ReportableEvent>();

  // Details (widgets etc) for Selected Event
  const chosenEventDetails = ref<EventType>();

  // Widgets by Event + Template
  const chosenWidgets = ref<WidgetObjectSorted[]>([]);

  const isDemoEvent = ref(false);
  const demoEventData = ref();
  const demoEventTemplate = ref();

  const isBenchmarking = ref(false);

  const getReportableEvents = () => {
    loading.value = true;

    getReportedEvents(userStore.accessToken).then((resp) => {
      reportableEvents.value = resp;

      if (chosenEventId.value) {
        chosenEvent.value = resp.find((evt: ReportableEvent) => evt.id === chosenEventId.value);
        getEventData();
      } else if (
        !userStore.userData?.vantage_tour_completed &&
        userStore.demoEvent &&
        !isDemoEvent.value
      ) {
        getDemoEventData()
          .then(() => {
            useDemoEvent();
            isDemoEvent.value = true;
          })
          .finally(() => {
            loading.value = false;
          });
      } else {
        loading.value = false;
      }
    });
  };

  const useDemoEvent = () => {
    const demoEventOption = {
      id: demoEventData.value.id,
      uuid: demoEventData.value.uuid,
      name: demoEventData.value.name,
    };

    if (!reportableEvents.value.length) {
      reportableEvents.value = [demoEventOption];
    } else if (reportableEvents.value[0].id !== demoEventOption.id) {
      reportableEvents.value.unshift(demoEventOption);
    }

    chosenEvent.value = reportableEvents.value.find((option) => option.id === demoEventOption.id);

    const orderedWidgets = demoEventTemplate.value.widgets.map(
      (widget: WidgetObject, index: number) => {
        const positionedWidget = ref({
          id: widget.id,
          x: widget.position.x,
          y: widget.position.y,
          h: widget.position.h,
          w: widget.position.w,
          i: index,
          widget: widget,
        });
        return positionedWidget.value;
      },
    );

    chosenWidgets.value = orderedWidgets;
  };

  const getEventData = async () => {
    const demoEventData = userStore.demoEvent ? userStore.demoEvent['CampaignsEvent'] : null;

    if (chosenEvent.value && (!demoEventData || chosenEvent.value.id !== demoEventData.id)) {
      loading.value = true;
      const eventId = chosenEvent.value.id;

      await getEventById(userStore.accessToken, eventId).then((resp) => {
        loading.value = false;
        chosenEventDetails.value = resp.CampaignsEvent;
        getEventWidgets();
      });
    }
  };

  const getDemoEventData = async () => {
    return await getDemoEventAnalytics(userStore.accessToken).then((resp) => {
      const { data } = resp;
      demoEventData.value = data.event['CampaignsEvent'];
      demoEventTemplate.value = data.template;
    });
  };

  const isDemoEventSelected = computed(() => {
    return demoEventData.value?.id === chosenEvent.value?.id;
  });

  watch(
    () => chosenEvent.value?.id,
    () => {
      updateParams();
      getEventData();
    },
  );

  const getEventWidgets = async () => {
    const template_id = chosenEventDetails.value?.template_id;
    if (chosenEvent.value && template_id) {
      loading.value = true;
      await getEventTemplateById(userStore.accessToken, chosenEvent.value.id, template_id).then(
        (resp) => {
          const orderedWidgets = filterAvailableWidgets(resp.widgets, userStore.hasFeature).map(
            (widget: WidgetOptions, index: number) => {
              // Sort Zone Options alphabetically
              if (widget.filters.area?.length) {
                const zone = widget.filters.area.find((area) => area.name === 'Zone');
                zone?.data?.sort((a, b) =>
                  (a.title?.toLowerCase() ?? '').localeCompare(b.title?.toLowerCase() ?? ''),
                );
              }

              const positionedWidget = ref({
                id: widget.id,
                x: widget.position.x,
                y: widget.position.y,
                h: widget.position.h,
                w: widget.position.w,
                i: index,
                widget: widget,
              });
              return positionedWidget.value;
            },
          );

          chosenWidgets.value = orderedWidgets;
          loading.value = false;
        },
      );
    }
  };

  getReportableEvents();

  // 60,000 milliseconds to 1 minute x 30 minutes;
  const timer = 60000 * 30;

  setInterval(() => {
    getReportableEvents();
  }, timer);

  function updateParams() {
    const params = ref<{ eventId?: number }>({});

    if (chosenEvent.value) {
      chosenEventId.value = chosenEvent.value.id;
      params.value.eventId = chosenEvent.value.id;
    }

    router.push({
      query: params.value,
    });
  }

  function getQueryParamsOnLoad() {
    chosenEventId.value = route.query.eventId ? parseInt(`${route.query.eventId}`) : null;
  }

  getQueryParamsOnLoad();

  return {
    chosenEvent,
    chosenEventDetails,
    chosenWidgets,

    isDemoEventSelected,
    isDemoEvent,
    getDemoEventData,
    useDemoEvent,

    loading,
    isBenchmarking,

    reportableEvents,
    getReportableEvents,

    getEventWidgets,
  };
});
